'use client';

import { User } from '@api/models';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';

import { ListingCardsScreen } from '@ui/sections/listings';
import { useAuthenticatedNavigation } from '@website/hooks/useAuthenticatedNavigation';
import { extractCity } from '@website/lib/address';
import { Activity } from '@website/services/activities.service';

interface ListingCardsScreenWrapperProps {
  /**
   * Whether to show the activity count text
   * @default true
   */
  showActivityCount?: boolean;

  /**
   * Whether to hide the instructor name on activity cards
   * @default false
   */
  hideInstructor?: boolean;

  /**
   * Array of activity objects to display
   */
  activities: {
    id: string;
    imageUrl: string;
    thumbnailUrl?: string;
    title: string;
    professionalName: string;
    isAtHome?: boolean;
    /** Real delivery-mode mix; 2+ kinds render as compact chips on the card. */
    locationKinds?: ('venue' | 'at_home' | 'online')[];
    /** Single-kind online-only flag (derived from `locationKinds`) so the card shows "Online", not "At Venue". */
    isOnline?: boolean;
    bookingsSinceLastYear: number;
    /** `null`/`undefined` → no priced slot yet; price block is hidden. */
    startingPrice?: number | null;
    /**
     * City/address shown when the activity is venue-based. Either `city`
     * or `location.address` may be provided - the wrapper falls back to
     * `location.address` when `city` is missing.
     */
    city?: string;
    location?: { address?: string } | null;
    /** The Hub the activity runs at; the card reads "at <Hub>". */
    hub?: { id: string; name: string } | null;
  }[];

  /**
   * Total count of activities (may be different from activities.length if paginated)
   */
  totalCount: number;

  /**
   * Whether the activities are currently loading
   */
  isLoading?: boolean;

  /**
   * Whether there was an error loading the activities
   */
  isError?: boolean;

  /**
   * Handler for when an activity card is hovered
   */
  onActivityHover?: (activityId: string) => void;

  /**
   * Handler for when hover leaves an activity card
   */
  onActivityLeaveHover?: () => void;

  /**
   * ID of the currently hovered activity to highlight it
   */
  hoveredActivityId?: string;

  /**
   * ID of the currently selected activity
   */
  selectedActivityId?: string;

  /**
   * Callback to register a ref for an activity card
   */
  registerCardRef?: (id: string, element: HTMLDivElement | null) => void;

  /**
   * Handler for when activity selection changes
   */
  onActivitySelect?: (activityId: string) => void;

  /**
   * Current user for authentication-aware navigation
   */
  user?: User | null;

  /** Suggestions listed below the results, each labelled by its `suggestionReason`. */
  suggestions?: Activity[];

  /**
   * Badge not-bookable-yet result cards ("No availability yet"). Off, they read
   * like any other card, minus the price and the slots action.
   * @default true
   */
  badgeUnavailable?: boolean;

  /**
   * Override for the empty-state body text (defaults to the generic
   * ListingCardsScreen.noActivitiesFound translation). Used by the search
   * page to explain context-specific empty results, e.g. that the active
   * location filter is the reason a keyword search came back empty.
   */
  noActivitiesFoundText?: string;

  /**
   * Optional action rendered under the empty-state message - e.g. a button
   * that moves the search to where matches were found.
   */
  emptyStateAction?: React.ReactNode;
}

/**
 * Client component wrapper for ListingCardsScreen
 * This handles the client-side interaction logic for the ListingCardsScreen component
 */
export const ListingCardsScreenWrapper: React.FC<ListingCardsScreenWrapperProps> = ({
  activities,
  totalCount,
  isLoading = false,
  isError = false,
  onActivityHover,
  onActivityLeaveHover,
  hoveredActivityId,
  selectedActivityId,
  registerCardRef,
  onActivitySelect,
  showActivityCount = true,
  hideInstructor = false,
  user,
  suggestions,
  badgeUnavailable = true,
  noActivitiesFoundText,
  emptyStateAction
}) => {
  const handleActivityClick = (id: string) => {
    console.log(`Activity ${id} selected`);

    // Always use the onActivitySelect handler if provided
    if (onActivitySelect) {
      onActivitySelect(id);
    }
    // Removed URL parameter manipulation for better client-side performance
  };

  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname.split('/')[1]; // Extract locale from current path

  // Authenticated navigation hook for slots
  const { navigateWithAuth } = useAuthenticatedNavigation({
    user: user ?? null
  });

  const getActivityHref = (id: string) => `/${locale}/activity/${id}`;

  const handleViewActivity = (id: string) => {
    console.log(`Navigate to activity ${id} details`);
    router.push(getActivityHref(id));
  };

  const handleViewSlots = (id: string) => {
    console.log(`Navigate to activity ${id} slots with auth`);
    // Use authenticated navigation for slots booking
    navigateWithAuth(`/activity/${id}/slots`);
  };

  const lsTranslations = useTranslations('ListingCardsScreen');
  const activityCardTranslations = useTranslations('ActivityCard');
  const commonTranslations = useTranslations('Common');

  const suggestionReasonLabels = {
    sameSearchElsewhere: lsTranslations('suggestionReason.sameSearchElsewhere'),
    nearby: lsTranslations('suggestionReason.nearby'),
    popular: lsTranslations('suggestionReason.popular')
  };

  // Shape a suggestion card
  const toExtraActivity = (act: Activity) => ({
    id: act.id,
    title: act.title,
    imageUrl: act.imageUrl,
    thumbnailUrl: act.thumbnailUrl,
    professionalName: act.professionalName,
    isAtHome: act.isAtHome,
    locationKinds: act.locationKinds,
    // Mirror ActivityDrawerContent: a single-kind online activity must label as
    // "Online" (Video icon), not fall through to "At Venue" (MapPin).
    isOnline: act.locationKinds?.length === 1 && act.locationKinds[0] === 'online',
    startingPrice: act.startingPrice,
    bookingsSinceLastYear: act.bookingsSinceLastYear || 0,
    city: extractCity(act.location?.address),
    hub: act.hub,
    availability: act.availability,
    contextLabel: act.suggestionReason ? suggestionReasonLabels[act.suggestionReason] : undefined
  });

  // Normalize address: callers may supply either `city` or `location.address`.
  // `ListingCardsScreen` only reads `city` and otherwise falls back to the
  // generic "Au lieu" label, so we resolve both shapes here in one place and
  // shorten to just the city to fit the card's tight location slot.
  const transformedActivities = activities.map(({ location, ...act }) => ({
    ...act,
    isOnline: act.locationKinds?.length === 1 && act.locationKinds[0] === 'online',
    city: extractCity(act.city ?? location?.address)
  }));

  return (
    <ListingCardsScreen
      activities={transformedActivities}
      totalCount={totalCount}
      isLoading={isLoading}
      isError={isError}
      showActivityCount={showActivityCount}
      hideInstructor={hideInstructor}
      onActivityClick={handleActivityClick}
      getActivityHref={getActivityHref}
      activitiesFoundSingularPrefix={lsTranslations('activitiesFoundSingularPrefix')}
      activitiesFoundSingularSuffix={lsTranslations('activitiesFoundSingularSuffix')}
      activitiesFoundPluralPrefix={lsTranslations('activitiesFoundPluralPrefix')}
      activitiesFoundPluralSuffix={lsTranslations('activitiesFoundPluralSuffix')}
      noActivitiesFoundTitle={lsTranslations('noActivitiesFoundTitle')}
      noActivitiesFoundText={noActivitiesFoundText ?? lsTranslations('noActivitiesFound')}
      loadingText={lsTranslations('loading')}
      errorText={commonTranslations('errorLoadingActivities')}
      atHomeText={activityCardTranslations('atHome')}
      atVenueText={activityCardTranslations('atVenue')}
      atHubText={(name) => activityCardTranslations('atHub', { name })}
      onlineText={activityCardTranslations('online')}
      priceLabel={activityCardTranslations('priceLabel')}
      bookingsInYearPrefix={activityCardTranslations('bookingsInYearPrefix')}
      bookingsInYearMiddle={activityCardTranslations('bookingsInYearMiddle')}
      bookingsInYearSuffix={activityCardTranslations('bookingsInYearSuffix')}
      onwardsText={activityCardTranslations('onwards')}
      professionalPrefixText={activityCardTranslations('professionalPrefix')}
      viewActivityButtonText={activityCardTranslations('viewActivityButton')}
      availableSlotsButtonText={activityCardTranslations('availableSlotsButton')}
      onActivityHover={onActivityHover}
      onActivityLeaveHover={onActivityLeaveHover}
      hoveredActivityId={hoveredActivityId}
      selectedActivityId={selectedActivityId}
      registerCardRef={registerCardRef}
      onViewActivity={handleViewActivity}
      onViewSlots={handleViewSlots}
      suggestions={suggestions?.map(toExtraActivity)}
      suggestionsTitle={lsTranslations('suggestionsTitle')}
      unavailableText={badgeUnavailable ? activityCardTranslations('notBookableYet') : undefined}
      emptyStateAction={emptyStateAction}
      locale={locale}
    />
  );
};
